import { useCallback, useEffect, useState } from "react";
import { Plus, Package, ClipboardList, RefreshCcw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import FloorInwardModal from "./FloorInwardModal";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import {
  getFloorBatches,
  getFloorRequests,
} from "../../../store/slices/Factory/newFloorSlice";
import { IFloorRequest } from "../../../models/floor.model";
import FloorRequestEditDetailsModal from "./FloorRequestItemsEditModal";
import NewFloorStock from "./NewFloorStock";
import NewFloorRequestsPage from "./NewFloorRequestsPage";
import FloorMaterialReturnRequestsPage from "./FloorMaterialReturnRequestsPage";

export default function FloorInventoryPage() {
  const dispatch: RootDispatch = useDispatch();
  const role = useSelector((state: RootState) => state.auth.role);
  const [searchParams, setSearchParams] = useSearchParams();

  const [mainTab, setMainTab] = useState<"request" | "stock" | "returns">(
    searchParams.get("subtab") === "request"
      ? "request"
      : searchParams.get("subtab") === "returns"
        ? "returns"
        : "stock",
  );

  const [isInwardModalOpen, setIsInwardModalOpen] = useState(false);
  const [requestTab, setRequestTab] = useState<
    "pending" | "approved" | "rejected" | "cancelled"
  >(() => {
    const status = searchParams.get("status");
    if (
      status === "pending" ||
      status === "approved" ||
      status === "rejected"
    ) {
      return status;
    }
    return "pending";
  });

  const [stockTab] = useState<"batches" | "all">(() => {
    const view = searchParams.get("view");
    return view === "all" ? "all" : "batches";
  });
  const [selectedRequest, setSelectedRequest] = useState<IFloorRequest>();
  const [floorRequestpage, setFloorRequestPage] = useState(1);

  const { requests, loading, pagination } = useSelector(
    (state: RootState) => state.newFloor,
  );

  const updateParams = (
    tab: "request" | "stock" | "returns",
    subTab?: string,
  ) => {
    const params = new URLSearchParams(searchParams);
    params.set("subtab", tab);

    if (tab === "request") {
      params.set("status", subTab ?? requestTab);
      params.delete("view");
    } else if (tab === "stock") {
      params.set("view", subTab ?? stockTab);
      params.delete("status");
    } else {
      params.delete("status");
    }
    setSearchParams(params);
  };

  const getRequestsCallback = useCallback(async () => {
    dispatch(
      getFloorRequests({
        params: {
          page: floorRequestpage,
          limit: 12,
          status: requestTab,
        },
      }),
    );
  }, [dispatch, floorRequestpage, requestTab]);

  useEffect(() => {
    if (mainTab !== "request") return;
    getRequestsCallback();
  }, [getRequestsCallback, mainTab]);

  const handleRequestTabChange = (
    status: "pending" | "approved" | "rejected" | "cancelled",
  ) => {
    setRequestTab(status);
    setFloorRequestPage(1); // Reset page context to 1 when changing filters
    updateParams("request", status);
  };
  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      {/* Header Panel */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
            Floor Inventory
          </h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">
            Manage inward requests and floor stock.
          </p>
        </div>

        <button
          className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-white font-medium shadow hover:bg-indigo-700 active:bg-indigo-800 transition text-sm md:text-base w-full sm:w-auto"
          onClick={() => setIsInwardModalOpen(true)}
        >
          <Plus size={18} />
          Add Floor Inward
        </button>
      </div>

      {/* Main Container Layout Card Wrapper */}
      <div className="rounded-2xl bg-white shadow-sm border border-slate-200 overflow-hidden">
        {/* Main Header Tabs */}
        <div className="border-b flex w-full">
          {role !== "factory" && (
            <MainTab
              active={mainTab === "request"}
              icon={<ClipboardList size={18} />}
              title="Request"
              onClick={() => {
                setMainTab("request");
                updateParams("request");
              }}
            />
          )}

          <MainTab
            active={mainTab === "stock"}
            icon={<Package size={18} />}
            title="Floor Stock"
            onClick={() => {
              setMainTab("stock");
              updateParams("stock");
            }}
          />
          <MainTab
            active={mainTab === "returns"}
            icon={<RefreshCcw size={18} />}
            title="Returns / Replacement"
            onClick={() => {
              setMainTab("returns");
              updateParams("returns");
            }}
          />
        </div>

        {/* Dynamic Inner Tab Router Contents switching conditional rendering view outputs */}
        {mainTab === "request" ? (
          <NewFloorRequestsPage
            requests={requests}
            loading={loading.getFloorRequestsLoading}
            pagination={pagination}
            requestTab={requestTab as any}
            floorRequestpage={floorRequestpage}
            setFloorRequestPage={setFloorRequestPage}
            onRequestTabChange={handleRequestTabChange}
            onSelectRequest={setSelectedRequest}
          />
        ) : mainTab === "returns" ? (
          <FloorMaterialReturnRequestsPage />
        ) : (
          <NewFloorStock />
        )}
      </div>

      {/* Actionable Modals Layers Controls */}
      <FloorInwardModal
        isOpen={isInwardModalOpen}
        onClose={async () => {
          setIsInwardModalOpen(false);
          if (mainTab === "request") {
            await getRequestsCallback();
          } else if (mainTab === "stock") {
            await dispatch(
              getFloorBatches({
                params: {
                  page: 1,
                  limit: 12,
                  search: "",
                },
              }),
            ).unwrap();
          }
        }}
      />

      {selectedRequest && (
        <FloorRequestEditDetailsModal
          onClose={async (success) => {
            setSelectedRequest(undefined);
            if (success) {
              await getRequestsCallback();
            }
          }}
          request={selectedRequest}
        />
      )}
    </div>
  );
}

function MainTab({
  active,
  title,
  icon,
  onClick,
}: {
  active: boolean;
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 md:px-6 py-4 font-medium transition border-b-2 text-sm md:text-base ${
        active
          ? "border-indigo-600 text-indigo-600 bg-indigo-50/50"
          : "border-transparent text-slate-500 hover:bg-slate-50"
      }`}
    >
      {icon}
      <span>{title}</span>
    </button>
  );
}
